import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import levelsData from './gameLevels.json';
import Timer from './Timer';
import LevelsList from './LevelsList';
import { getGameLevels } from './services/gameLevelsService';
import Tile from './Tile';
import { TILE_TYPES } from './constants/gameConstants';
import { useSwipeable } from 'react-swipeable';

var GRID_SIZE = 0;

function SwipeableWallTile({ row, col, children, moveWall }) {
  const handlers = useSwipeable({
    onSwipedLeft: () => moveWall(row, col, 'left'),
    onSwipedRight: () => moveWall(row, col, 'right'),
    onSwipedUp: () => moveWall(row, col, 'up'),
    onSwipedDown: () => moveWall(row, col, 'down'),
    trackTouch: true,
    trackMouse: false,
    preventDefaultTouchmoveEvent: true,
  });
  return (
    <div {...handlers} style={{ touchAction: 'none' }}>
      {children}
    </div>
  );
}

const BunnyGame = () => {
  const [grid, setGrid] = useState(Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(TILE_TYPES.EMPTY)));
  const [draggedTile, setDraggedTile] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  const [bunnyInitial, setBunnyInitial] = useState([0, 0]);
  const [bunnyMoving, setBunnyMoving] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(null);
  const levels = getGameLevels();

  const handleLevelSelect = (levelIndex) => {    
    setCurrentLevel(levelIndex);    
    const selectedLevel = levels[levelIndex];
    GRID_SIZE = selectedLevel.grid.length;
    setGrid(selectedLevel.grid);
    setGameStarted(false);
    setBunnyMoving(false);
    if (intervalId) clearInterval(intervalId);
  };

  useEffect(() => {
    if (gameStarted) {
      const id = setInterval(() => moveMonsters(), 1000);
      setIntervalId(id);
      return () => clearInterval(id);
    } else {
      if (intervalId) clearInterval(intervalId);
    }
  }, [gameStarted]);

  const removeBunny = (grid) => {
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (grid[row][col] === TILE_TYPES.BUNNY) {
          grid[row][col] = TILE_TYPES.EMPTY;
        }
      }
    }
    setGrid(grid);
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const moveMonsters = () => {
    setGrid(prevGrid => {
      const newGrid = prevGrid.map(row => [...row]);
      let bunnyCaught = false;

      const directions = [
        [0, 1],  // Right
        [1, 0],  // Down
        [0, -1], // Left
        [-1, 0], // Up
      ];

      const newMonsterPositions: [number, number][] = [];

      const occupiedPositions = new Set();

      for (let row = 0; row < GRID_SIZE; row++) {
        for (let col = 0; col < GRID_SIZE; col++) {
          if (prevGrid[row][col] !== TILE_TYPES.MONSTER) continue;

          let moved = false;

          for (const [dr, dc] of shuffleArray(directions)) {
            const newRow = row + dr;
            const newCol = col + dc;

            if (
              newRow >= 0 && newRow < GRID_SIZE &&
              newCol >= 0 && newCol < GRID_SIZE &&
              (prevGrid[newRow][newCol] === TILE_TYPES.EMPTY || prevGrid[newRow][newCol] === TILE_TYPES.BUNNY) &&
              !occupiedPositions.has(`${newRow},${newCol}`)
            ) {
              if (prevGrid[newRow][newCol] === TILE_TYPES.BUNNY) {
                bunnyCaught = true;
              }

              newGrid[row][col] = TILE_TYPES.EMPTY;
              newMonsterPositions.push([newRow, newCol]);
              occupiedPositions.add(`${newRow},${newCol}`);
              moved = true;
              break; // monster moved, break from directions
            }
          }

          if (!moved) {
            newMonsterPositions.push([row, col]);
            occupiedPositions.add(`${row},${col}`);
          }
        }
      }

      for (const [r, c] of newMonsterPositions) {
        newGrid[r][c] = TILE_TYPES.MONSTER;
      }

      if (bunnyCaught) {
        removeBunny(newGrid);
        alert('Game Over! The monster caught the bunny.');
        setGameStarted(false);
        setBunnyMoving(false);
      }

      return newGrid;
    });
  };

  const isBunnyReachableByMonsters = (grid) => {
    const visited = new Set();
    const directions = [
      [0, 1], [1, 0], [0, -1], [-1, 0],
    ];

    const dfs = (r, c) => {
      if (
        r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE ||
        visited.has(`${r},${c}`) || (grid[r][c] === TILE_TYPES.WALL || grid[r][c] === TILE_TYPES.FIXED)
      ) return false;

      if (grid[r][c] === TILE_TYPES.BUNNY) return true;

      visited.add(`${r},${c}`);

      for (const [dr, dc] of directions) {
        if (dfs(r + dr, c + dc)) return true;
      }

      return false;
    };

    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (grid[row][col] === TILE_TYPES.MONSTER) {
          if (dfs(row, col)) return true;
        }
      }
    }

    return false;
  };

  const moveBunnyAlongPath = (startRow, startCol) => {
    if (bunnyMoving) return;


    if (isBunnyReachableByMonsters(grid)) {
      alert('No safe path for the bunny!');
      return;
    }

    setBunnyMoving(true);

    const visited = new Set();
    const path = [];

    const dfs = (r, c) => {
      if (
        r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE ||
        visited.has(`${r},${c}`) || (grid[r][c] !== TILE_TYPES.BUNNY && grid[r][c] !== TILE_TYPES.EMPTY && grid[r][c] !== TILE_TYPES.EXIT)
      ) return false;

      visited.add(`${r},${c}`);
      path.push([r, c]);

      if (grid[r][c] === TILE_TYPES.EXIT) return true;

      const directions = [
        [0, 1], [1, 0], [0, -1], [-1, 0],
      ];

      for (let [dr, dc] of directions) {
        if (dfs(r + dr, c + dc)) return true;
      }

      path.pop();
      return false;
    };

    setTimeout(() => {
      const newGrid = grid.map(row => [...row]);
      let [curRow, curCol] = [startRow, startCol];
      newGrid[curRow][curCol] = TILE_TYPES.EMPTY;

      if (dfs(startRow, startCol)) {
        let index = 0;
        const moveStep = () => {
          const [r, c] = path[index];
          if (newGrid[r][c] === TILE_TYPES.MONSTER) {
            removeBunny(newGrid);
            alert('Game Over! The bunny was eaten during movement.');
            setGameStarted(false);
            setBunnyMoving(false);
            return;
          }
          newGrid[curRow][curCol] = TILE_TYPES.EMPTY;
          newGrid[r][c] = TILE_TYPES.BUNNY;
          setGrid(newGrid.map(row => [...row]));
          curRow = r;
          curCol = c;

          if (++index < path.length) {
            setTimeout(moveStep, 500);
          } else {
            setBunnyMoving(false);
            alert('Bunny reached the exit! You Won!');
            setGameStarted(false);
            if(currentLevel < levels.length - 1) {
              handleLevelSelect(currentLevel+1);
            }
            
          }
        };
        moveStep();
      } else {
        alert('No path to exit. Bunny returns to start.');
        newGrid[startRow][startCol] = TILE_TYPES.BUNNY;
        setGrid(newGrid);
        setBunnyMoving(false);
      }
    }, 300);
  };

  const handleStart = () => {
    setGameStarted(true);
  };

  const handleReset = () => {
    handleLevelSelect(currentLevel);
  };

  const handleDragStart = (e, row, col) => {
    if (!gameStarted) return;
    setDraggedTile({ row, col, type: grid[row][col] });
  };

  const handleDrop = (e, targetRow, targetCol) => {
    e.preventDefault();
    if (!draggedTile || !gameStarted) return;

    setGrid(prev => {
      const newGrid = prev.map(row => [...row]);

      if (newGrid[targetRow][targetCol] === TILE_TYPES.EMPTY) {
        newGrid[targetRow][targetCol] = draggedTile.type;
        newGrid[draggedTile.row][draggedTile.col] = TILE_TYPES.EMPTY;
      }
      return newGrid;
    });

    setDraggedTile(null);
  };

  const handleDragOver = (e) => {
    if (gameStarted) e.preventDefault();
  };

  const handleBunnyClick = (row, col) => {
    if (gameStarted && grid[row][col] === TILE_TYPES.BUNNY) {
      moveBunnyAlongPath(row, col);
    }
  };

  const isMovableWall = (type) => {
    return type === TILE_TYPES.WALL;
  };

  const moveWall = (row, col, direction) => {
    if (!gameStarted) return;
    let dRow = 0, dCol = 0;
    if (direction === 'up') dRow = -1;
    if (direction === 'down') dRow = 1;
    if (direction === 'left') dCol = -1;
    if (direction === 'right') dCol = 1;
    const newRow = row + dRow;
    const newCol = col + dCol;
    if (
      newRow >= 0 && newRow < GRID_SIZE &&
      newCol >= 0 && newCol < GRID_SIZE &&
      grid[newRow][newCol] === TILE_TYPES.EMPTY
    ) {
      setGrid(prev => {
        const newGrid = prev.map(r => [...r]);
        newGrid[newRow][newCol] = TILE_TYPES.WALL;
        newGrid[row][col] = TILE_TYPES.EMPTY;
        return newGrid;
      });
    }
  };

  return (
   <div className="flex flex-col lg:flex-row justify-center items-start p-4 bg-gray-900 min-h-screen text-gray-100">
         {/* Game Board Section */}
         <div className="w-full lg:w-3/4 flex flex-col items-center bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
           <h2 className="text-3xl font-extrabold text-purple-400 mb-6 drop-shadow-md">Bunny Escape</h2>
           <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6 w-full">
             <button
               onClick={handleStart}
               disabled={!grid.length || gameStarted || bunnyMoving}
               className="w-full sm:w-auto px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full shadow-md transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75"
             >
               {gameStarted ? 'Game Running' : 'Start Game'}
             </button>
             <button
               onClick={handleReset}
               disabled={!grid.length || bunnyMoving}
               className="w-full sm:w-auto px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-full shadow-md transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75"
             >
               Reset Game
             </button>
             <Timer gameStarted={gameStarted} />
           </div>
   
           {!grid.length ? (
             <div className="p-8 text-center text-lg text-gray-400">
               Please select a level to start the game.
             </div>
           ) : (
             <div
               className="grid gap-0"
               style={{
                 gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))`,
                 width: `${GRID_SIZE * 48}px`, // Adjust tile size if needed
                 height: `${GRID_SIZE * 48}px`,
               }}              
             >
               {grid.map((row, rowIndex) => (
                 <React.Fragment key={rowIndex}>
                   {row.map((cell, colIndex) => {
                     if (isMovableWall(cell)) {
                       return (
                         <SwipeableWallTile
                           key={`${rowIndex}-${colIndex}`}
                           row={rowIndex}
                           col={colIndex}
                           moveWall={moveWall}
                         >
                           <Tile
                             type={cell}
                             row={rowIndex}
                             col={colIndex}
                             onDragStart={handleDragStart}
                             onDrop={handleDrop}
                             onDragOver={handleDragOver}
                             gameStarted={gameStarted}
                             onBunnyClick={handleBunnyClick}
                             GRID_SIZE={GRID_SIZE}
                           />
                         </SwipeableWallTile>
                       );
                     }
                     return (
                       <div
                         key={`${rowIndex}-${colIndex}`}
                       >
                         <Tile
                           type={cell}
                           row={rowIndex}
                           col={colIndex}
                           onDragStart={handleDragStart}
                           onDrop={handleDrop}
                           onDragOver={handleDragOver}
                           gameStarted={gameStarted}
                           onBunnyClick={handleBunnyClick}
                           GRID_SIZE={GRID_SIZE}
                         />
                       </div>
                     );
                   })}
                 </React.Fragment>
               ))}
             </div>
           )}

           {/* Game Rules Section */}
           <div className="text-xs text-gray-400 mt-4 text-center">
             <p>Game Rules:</p>
             <ul className="list-disc list-inside">
               <li>Help Bunny to reach home.</li>
               <li>Drag and drop tiles to trap monsters and create a safe path for the bunny.</li>
               <li>Avoid the monsters; if they catch the bunny, the game is over.</li>               
               <li>Once safe path is created, click bunny to move.</li>
               <li>Fixed walls cannot be moved.</li>
             </ul>
           </div>
         </div>

         {/* Levels List Section */}
         <div className="w-full lg:w-1/4 p-4 mb-8 lg:mb-0 lg:mr-8 bg-gray-800 rounded-xl shadow-lg border border-gray-700">
           <LevelsList onLevelSelect={handleLevelSelect} currentLevelIndex={currentLevel} />
         </div>
       </div>
  );
};

export default BunnyGame;


